// Typed fetch wrappers + payload types matching SQLite columns.

export type ProductKind =
  | "filament" | "resin" | "printer" | "printer_part"
  | "3d_scanner" | "laser_engraver" | "forming_machine"
  | "post_processor" | "resin_additive" | "consumable"
  | "lab_equipment" | "office_equipment" | "document" | "other";

export type MaterialFamily = "PLA"|"PETG"|"ABS"|"TPU"|"PC"|"ASA"|"Nylon"|"PVA"|"HIPS"|"Resin"|"Other";
export type MaterialSubtype = "Premium"|"Silk"|"Matt"|"Carbon Fiber Reinforced"|"High Speed"|"ReFill"|"Pro"|"Standard"|"Other";
export type ColorFamily = "Black"|"White"|"Grey"|"Blue"|"Red"|"Green"|"Yellow"|"Orange"|"Purple"|"Pink"|"Brown"|"Metallic"|"Transparent"|"Multi"|"Other";
export type ConditionGrade = "new"|"excellent"|"good"|"used"|"demonstration";
export type PhotoKind = "hero"|"angle"|"label_closeup"|"barcode_closeup"|"spool"|"defect";

export type ProductRow = {
  id: number;
  uid: string;
  name: string;
  brand: string | null;
  color_material: string | null;
  quantity: number | null;
  quantity_source: "counted" | "overlay" | "unknown" | null;
  notes: string | null;
  batch: string;
  group_key: string;
  status: "needs_review" | "done" | "error";
  processed_at: string;
  created_at: string;
  updated_at: string;
  // sales-metadata structured fields
  ean13: string | null;
  manufacturer_sku: string | null;
  material_family: MaterialFamily | null;
  material_subtype: MaterialSubtype | null;
  color_name: string | null;
  color_family: ColorFamily | null;
  color_hex: string | null;
  diameter_mm: number | null;
  net_weight_g: number | null;
  nozzle_temp_min_c: number | null;
  nozzle_temp_max_c: number | null;
  bed_temp_min_c: number | null;
  bed_temp_max_c: number | null;
  country_of_origin: string | null;
  warehouse_location: string | null;
  product_kind: ProductKind | null;
  parent_product_id: number | null;
  archived_at: string | null;
  // computed
  total_quantity: number;
  child_count: number;
  price_median: number | null;
  price_half_range: number | null;
  price_count: number;
  primary_photo_id: number | null;
  barcode_count: number;
  bbox: string | null;
  source_photo_id: number | null;
  price_override_minor: number | null;
  condition_grade: ConditionGrade | null;
  condition_adj_enabled: 0 | 1;
  comments: Array<{ id: number; username: string; body: string }>;
};

export type ChildRow = {
  uid: string;
  name: string;
  quantity: number | null;
  batch: string;
  warehouse_location: string | null;
};

export type ProductDetail = {
  product: ProductRow & {
    photo_analysis: string | null;
    model: string;
  };
  photos: PhotoRow[];
  links: LinkRow[];
  duplicates: DuplicatePair[];
  parent: { uid: string; name: string } | null;
  children: ChildRow[];
};

export type PhotoRow = {
  id: number;
  photo_path: string;
  cached_jpg: string | null;
  sha256: string;
  mime_type: string;
  width: number | null;
  height: number | null;
  phash: string | null;
  sharpness: number | null;
  aae_path: string | null;
  is_primary: 0 | 1;
  photo_kind: PhotoKind | null;
  barcode_value: string | null;
  barcode_type: string | null;
  bbox: string | null;
};

export type LinkRow = {
  id: number;
  url: string;
  source_kind: "manufacturer" | "seller" | "reference";
  source_name: string | null;
  price_minor: number | null;
  currency: string;
  price_aud_minor: number | null;
  fx_rate: number | null;
  fx_fetched_at: string | null;
  fetched_at: string;
  notes: string | null;
};

export type ProductComment = {
  id: number;
  username: string;
  body: string;
  created_at: string;
};

export type DuplicatePair = {
  product_a_id: number;
  product_b_id: number;
  score: number;
  resolution: "open" | "merged" | "dismissed";
  a_uid: string;
  a_name: string;
  b_uid: string;
  b_name: string;
};

export type DuplicateCard = DuplicatePair & {
  a_brand: string | null;
  a_color: string | null;
  a_qty: number | null;
  a_photo_id: number | null;
  a_batch: string;
  a_price_avg: number | null;
  a_kind: string | null;
  b_brand: string | null;
  b_color: string | null;
  b_qty: number | null;
  b_photo_id: number | null;
  b_batch: string;
  b_price_avg: number | null;
  b_kind: string | null;
};

export type ReviewChild = { uid: string; name: string; quantity: number | null };

export type SplitChildSpec = Partial<Pick<ProductRow,
    "name" | "brand" | "quantity" | "quantity_source" | "notes"
  | "ean13" | "manufacturer_sku" | "material_family" | "material_subtype"
  | "color_name" | "color_family" | "color_hex" | "diameter_mm" | "net_weight_g"
  | "nozzle_temp_min_c" | "nozzle_temp_max_c" | "bed_temp_min_c" | "bed_temp_max_c"
  | "country_of_origin" | "warehouse_location" | "product_kind">> & {
  bbox?: [number, number, number, number];
};

export type ReviewItem = ProductRow & {
  confidence: number | null;
  primary_photo_id: number | null;
  primary_cached_jpg: string | null;
  primary_photo_bbox: string | null;
  manual_price_minor: number | null;
  parent_uid: string | null;
  parent_name: string | null;
  children: ReviewChild[];
};

export type BatchSummary = {
  batch: string;
  total: number;
  done: number;
  needs_review: number;
  errors: number;
  ean13_count: number;
  disk_units: number | null;
  uncatalogued: number;
};

export type ProductListResp = {
  items: ProductRow[];
  total: number;
  page: number;
  per_page: number;
  total_value_aud: number | null;
  condition_adjusted_value_aud: number | null;
};

async function json<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const r = await fetch(input, init);
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return (await r.json()) as T;
}

export const api = {
  products(params: Record<string, string | number | undefined> = {}, kinds: ProductKind[] = []) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== "") qs.set(k, String(v));
    }
    kinds.forEach(k => qs.append("kind", k));
    return json<ProductListResp>(`/api/products?${qs}`);
  },
  product(uid: string) {
    return json<ProductDetail>(`/api/products/${encodeURIComponent(uid)}`);
  },
  patchProduct(uid: string, fields: Partial<Pick<ProductRow,
      "name" | "brand" | "color_material" | "quantity" | "quantity_source" | "notes" | "status"
    | "ean13" | "manufacturer_sku" | "material_family" | "material_subtype"
    | "color_name" | "color_family" | "color_hex" | "diameter_mm" | "net_weight_g"
    | "nozzle_temp_min_c" | "nozzle_temp_max_c" | "bed_temp_min_c" | "bed_temp_max_c"
    | "country_of_origin" | "warehouse_location" | "product_kind"
    | "parent_product_id" | "bbox"
    | "price_override_minor" | "condition_grade" | "condition_adj_enabled">>) {
    return json<{ ok: boolean; reason?: string }>(
      `/api/products/${encodeURIComponent(uid)}`,
      { method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(fields) },
    );
  },
  productByEan(ean13: string) {
    return json<import("./api").ProductDetail>(`/api/products/by-ean/${encodeURIComponent(ean13)}`);
  },
  batchesSummary() {
    return json<BatchSummary[]>(`/api/products/batches/summary`);
  },
  brands() {
    return json<string[]>(`/api/products/brands`);
  },
  archive(uid: string) {
    return json<{ ok: boolean }>(`/api/products/${encodeURIComponent(uid)}/archive`, { method: "POST" });
  },
  restore(uid: string) {
    return json<{ ok: boolean }>(`/api/products/${encodeURIComponent(uid)}/restore`, { method: "POST" });
  },
  setQuickPrice(uid: string, priceAud: number | null) {
    return json<{ ok: boolean }>(
      `/api/products/${encodeURIComponent(uid)}/quick-price`,
      { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ price_aud: priceAud }) },
    );
  },
  splitProduct(uid: string, children: SplitChildSpec[]) {
    return json<{ ok: boolean; uids: string[] }>(
      `/api/products/${encodeURIComponent(uid)}/split`,
      { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ children }) },
    );
  },
  duplicates(params: { skipSameBatch?: boolean } = {}) {
    const qs = params.skipSameBatch ? "?skip_same_batch=1" : "";
    return json<DuplicateCard[]>(`/api/duplicates${qs}`);
  },
  resolveDuplicate(a: number, b: number, payload: { action: "merge" | "link" | "dismiss"; winner_uid?: string }) {
    return json<{ ok: boolean; reason?: string }>(
      `/api/duplicates/${a}/${b}/resolve`,
      { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload) },
    );
  },
  async exportCsv(params: Record<string, string | number | undefined> = {}): Promise<Blob> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params))
      if (v !== undefined && v !== "") qs.set(k, String(v));
    const r = await fetch(`/api/export.csv?${qs}`);
    if (!r.ok) {
      let msg = `${r.status} ${r.statusText}`;
      const j = await r.json().catch(() => null) as { error?: string } | null;
      if (j?.error) msg = j.error;
      throw new Error(msg);
    }
    return r.blob();
  },
  reviewQueue(params: { batch?: string; status?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.batch) qs.set("batch", params.batch);
    if (params.status) qs.set("status", params.status);
    return json<ReviewItem[]>(`/api/products/review-queue?${qs}`);
  },
  comments(uid: string) {
    return json<ProductComment[]>(`/api/products/${encodeURIComponent(uid)}/comments`);
  },
  addComment(uid: string, payload: { username: string; body: string }) {
    return json<ProductComment>(
      `/api/products/${encodeURIComponent(uid)}/comments`,
      { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload) },
    );
  },
  photoUrl(id: number) {
    return `/api/photo/${id}`;
  },
};
